import type { User } from '@auth0/auth0-react';
import type { ToOptions } from '@tanstack/react-router';

import type {
  EnterpriseDomain,
  EnterpriseSettings,
} from 'src/api/endpoints/cloudEnterpriseAdminApi.types';
import type { EnterpriseEnrollmentStatus } from 'src/api/endpoints/cloudEnterpriseEnrollmentApi.types';
import { EnterpriseEnrollmentStep } from 'src/api/endpoints/cloudEnterpriseEnrollmentApi.types';

import type { CloudConnectSubscription } from 'src/api/endpoints/cloudConnect.types';
import type { MembersQueryData } from 'src/api/queries/enterpriseAdmin';
import type { RmClaims } from 'src/api/queries/user';
import { URLS } from './urls/urls';
import { userHelpers } from './userHelpers';

const getPrimaryDomain = (orgSettings?: EnterpriseSettings | null) =>
  orgSettings?.domains?.find((domain) => domain.isPrimary === true) ??
  undefined;

const isMigrationEnabled = (domain?: EnterpriseDomain) =>
  !!domain?.migrationEnabled;

const isDomainForwardingEnabled = (domain?: EnterpriseDomain) =>
  !!domain?.homeRealmDiscoveryEnabled;

const getEnrollmentStep = (
  enrollmentStatus?: EnterpriseEnrollmentStatus | null,
  user?: User
): EnterpriseEnrollmentStep => {
  if (!enrollmentStatus || !user) {
    return EnterpriseEnrollmentStep.NotStarted;
  }

  if (enrollmentStatus.enrollment === 'FINALIZED') {
    return EnterpriseEnrollmentStep.Finalized;
  }

  const hasMatchingOrgConnection =
    userHelpers.getOrganizationConnection(user) ===
    enrollmentStatus.connectionId;

  if (
    enrollmentStatus.signInUrl &&
    enrollmentStatus.base64Cert &&
    hasMatchingOrgConnection &&
    userHelpers.getOrganizationId(user)
  ) {
    return EnterpriseEnrollmentStep.Confirm;
  }

  if (enrollmentStatus.signInUrl && enrollmentStatus.base64Cert) {
    return EnterpriseEnrollmentStep.VerifySAML;
  }

  if (enrollmentStatus.domainVerification === 'OK') {
    return EnterpriseEnrollmentStep.SetupSAML;
  }

  if (enrollmentStatus.primaryDomain) {
    return EnterpriseEnrollmentStep.VerifyDomain;
  }

  return EnterpriseEnrollmentStep.CreateOrganization;
};

const getEnrollmentStepUrl = (
  step: EnterpriseEnrollmentStep
): ToOptions['to'] => {
  switch (step) {
    case EnterpriseEnrollmentStep.Finalized:
    case EnterpriseEnrollmentStep.Confirm:
      return URLS.ENTERPRISE_CONFIRM_SETUP;
    case EnterpriseEnrollmentStep.VerifySAML:
      return URLS.ENTERPRISE_VERIFY_SAML;
    case EnterpriseEnrollmentStep.SetupSAML:
      return URLS.ENTERPRISE_SETUP_SAML;
    case EnterpriseEnrollmentStep.VerifyDomain:
      return URLS.ENTERPRISE_VERIFY_DOMAIN;
    case EnterpriseEnrollmentStep.CreateOrganization:
      return URLS.ENTERPRISE_CREATE_ORGANIZATION;
    case EnterpriseEnrollmentStep.NotStarted:
    default:
      return URLS.ENTERPRISE_SETUP;
  }
};

const getVisibleEnrollmentSteps = () =>
  Object.values(EnterpriseEnrollmentStep).slice(1, -1);

const getEnrollmentStepName = (step: EnterpriseEnrollmentStep) => {
  switch (step) {
    case EnterpriseEnrollmentStep.Finalized:
    case EnterpriseEnrollmentStep.Confirm:
      return 'Complete';
    case EnterpriseEnrollmentStep.VerifySAML:
      return 'Verify SAML';
    case EnterpriseEnrollmentStep.SetupSAML:
      return 'Connect your IdP';
    case EnterpriseEnrollmentStep.VerifyDomain:
      return 'Verify domain';
    case EnterpriseEnrollmentStep.CreateOrganization:
      return 'Organization';
    case EnterpriseEnrollmentStep.NotStarted:
    default:
      return '';
  }
};

const isEnrollmentStepCompleted = (
  step: EnterpriseEnrollmentStep,
  enrollmentStatus: EnterpriseEnrollmentStatus
) => step < getEnrollmentStep(enrollmentStatus);

const canRequestToCancelSubscription = (
  accessTokenClaims: RmClaims | undefined,
  subscription: CloudConnectSubscription | null | undefined
) => {
  if (
    !accessTokenClaims ||
    !subscription ||
    !userHelpers.hasPermissionDeleteSubscription(accessTokenClaims) ||
    subscription.status === 'canceled' ||
    !!subscription.cancelAt ||
    subscription.cancelRequested === true
  ) {
    return false;
  }

  return true;
};

const canAbortRequestToCancelSubscription = (
  accessTokenClaims: RmClaims | undefined,
  subscription: CloudConnectSubscription | null | undefined
) => {
  if (
    !accessTokenClaims ||
    !subscription ||
    !userHelpers.hasPermissionUpdateSubscription(accessTokenClaims) ||
    subscription.status === 'canceled' ||
    !!subscription.cancelAt ||
    subscription.cancelRequested === false
  ) {
    return false;
  }

  return true;
};

const canStartOffboarding = (
  accessTokenClaims: RmClaims | undefined,
  orgSettings: EnterpriseSettings | null | undefined
) => {
  if (
    !accessTokenClaims ||
    !orgSettings ||
    !userHelpers.hasPermissionCreateOffboarding(accessTokenClaims) ||
    orgSettings.offboardingStatus !== 'OFFBOARDABLE'
  ) {
    return false;
  }

  return true;
};

const hasStartedOffboarding = (
  orgSettings: EnterpriseSettings | null | undefined
) =>
  orgSettings?.state === 'OFFBOARDING' &&
  orgSettings.offboardingStatus !== 'IDLE' &&
  orgSettings.offboardingStatus !== 'OFFBOARDABLE';

function removeDeviceFromMemberData(
  data: MembersQueryData,
  memberId: string,
  deviceId: string
): MembersQueryData {
  if (!data) return data;

  return {
    ...data,
    users: data.users?.map((user) => {
      if (user.id !== memberId) {
        return user;
      }

      return {
        ...user,
        devices: user.devices?.filter(
          (device) => device.serialNumber !== deviceId
        ),
      };
    }),
  } satisfies MembersQueryData;
}

const dataHelpers = {
  removeDeviceFromMemberData,
};

export const enterpriseHelpers = {
  canRequestToCancelSubscription,
  canAbortRequestToCancelSubscription,
  canStartOffboarding,
  hasStartedOffboarding,
  getEnrollmentStep,
  getEnrollmentStepName,
  getEnrollmentStepUrl,
  getPrimaryDomain,
  getVisibleEnrollmentSteps,
  isDomainForwardingEnabled,
  isEnrollmentStepCompleted,
  isMigrationEnabled,
  dataHelpers,
};
